import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { FaMapMarkerAlt, FaUsers } from 'react-icons/fa';
import '../styles/DamanPage.css'; 
import ClientBranches from './ClientBranches';
import CompanyProfile1 from './CompanyProfile1';

const DamanClientsPage = () => {
  const [visibleCards, setVisibleCards] = useState(0);
  const [counters, setCounters] = useState({
    clients: 0,
    peopleServed: 0,
    locations: 0
  });

  // Refs for Intersection Observer to animate hero text
  const dcpHeroTitleRef = useRef(null);
  const dcpHeroBreadcrumbRef = useRef(null);

  // Daman specific client data
  const damanClients = [
    { name: "NELSON GLOBAL INDIA LTD.", location: "Dalwada", persons: 450, logo: "/clients/Nelson-Global-1.png", industry: "Manufacturing" },
    { name: "SUZLON ENERGY LTD. PANCHAL", location: "Panchal", persons: 300, logo: "/clients/Suzlon-3.png", industry: "Energy" },
    { name: "SUZLON ENERGY LTD.", location: "Dhabel", persons: 200, logo: "/clients/Suzlon-3.png", industry: "Energy" },
    { name: "SUZLON ENERGY LTD.", location: "Dalwada", persons: 600, logo: "/clients/Suzlon-3.png", industry: "Energy" },
    { name: "SUZLON ENERGY LTD.", location: "Beslor", persons: 1100, logo: "/clients/Suzlon-3.png", industry: "Energy" },
    { name: "Zobele India PVT. LTD.", location: "Panchal", persons: 800, logo: "/clients/Zobele-7.png", industry: "Consumer Goods" },
    { name: "POLYCAB WIRES PVT LTD.", location: "Daman", persons: 500, logo: "/clients/polycab-wires-13.png", industry: "Electrical" },
    // { name: "POLYCAB WIRES PVT LTD. (WARE HOUSE)", location: "Daman", persons: 200, logo: "/clients/polycab-wires-13.png", industry: "Logistics" }
  ];

  // Calculate total people served and unique locations for Daman
  const totalPeopleServed = damanClients.reduce((sum, client) => sum + client.persons, 0);
  const totalLocations = [...new Set(damanClients.map(client => client.location))].length;

  // Animate counters on component mount
  useEffect(() => {
    const animateCounters = () => {
      const duration = 2000;
      const steps = 60;
      const stepTime = duration / steps;

      let currentStep = 0;
      const interval = setInterval(() => {
        currentStep++;
        const progress = currentStep / steps;

        setCounters({
          clients: Math.floor(damanClients.length * progress),
          peopleServed: Math.floor(totalPeopleServed * progress),
          locations: Math.floor(totalLocations * progress)
        });

        if (currentStep >= steps) {
          clearInterval(interval);
          setCounters({
            clients: damanClients.length,
            peopleServed: totalPeopleServed,
            locations: totalLocations
          });
        }
      }, stepTime);
    };
    animateCounters();
  }, [damanClients.length, totalPeopleServed, totalLocations]);

  // Animate client cards appearing in a staggered manner
  useEffect(() => {
    const timer = setTimeout(() => {
      if (visibleCards < damanClients.length) {
        setVisibleCards(prev => prev + 1);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [visibleCards, damanClients.length]);

  // Callback for Intersection Observer to add/remove animation class
  const handleIntersect = useCallback((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add("dcp-animate-in");
      } else {
        entry.target.classList.remove("dcp-animate-in");
      }
    });
  }, []);

  // Set up Intersection Observers for hero text elements
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: "0px",
      threshold: 0.2,
    };
    const observer = new IntersectionObserver(handleIntersect, observerOptions);

    const currentHeroTitleRef = dcpHeroTitleRef.current;
    const currentHeroBreadcrumbRef = dcpHeroBreadcrumbRef.current;

    if (currentHeroTitleRef) observer.observe(currentHeroTitleRef);
    if (currentHeroBreadcrumbRef) observer.observe(currentHeroBreadcrumbRef);

    return () => {
      if (currentHeroTitleRef) observer.unobserve(currentHeroTitleRef);
      if (currentHeroBreadcrumbRef) observer.unobserve(currentHeroBreadcrumbRef);
    };
  }, [handleIntersect]);

  return (
    <>
    <Helmet>
       <title>Clients in Daman | Trusted Partners Across Industries | Laxmi Hospitality Services</title>
       <meta
         name="description"
         content="Explore Laxmi Hospitality Services' extensive client partnerships in Daman union territory. Serving diverse industries including Energy, Manufacturing, Consumer Goods, and Electrical, we empower thousands with reliable, scalable catering solutions."
       />
       <meta name="robots" content="index, follow" />
       <meta name="viewport" content="width=device-width, initial-scale=1.0" />
       <link rel="canonical" href="https://laxmihospitalityservices.in/clients/daman" />
     
       {/* -------- OPEN GRAPH (SOCIAL) -------- */}
       <meta property="og:type" content="website" />
       <meta property="og:url" content="https://laxmihospitalityservices.in/clients/daman" />
       <meta property="og:title" content="Clients in Daman | Trusted Partners Across Industries | Laxmi Hospitality Services" />
       <meta property="og:description" content="Discover our trusted client partnerships in Daman. Serving 7 leading companies across 5 locations with 3,950+ people served daily." />
       <meta property="og:site_name" content="Laxmi Hospitality Services" />
       <meta property="og:locale" content="en_IN" />
       <meta property="og:image" content="https://laxmihospitalityservices.in/images/Daman.png" />
     
       {/* -------- DAMAN CLIENTS COLLECTION PAGE -------- */}
       <script type="application/ld+json">
         {`
           {
             "@context": "https://schema.org",
             "@type": "CollectionPage",
             "@id": "https://laxmihospitalityservices.in/clients/daman",
             "name": "Daman Clients - Laxmi Hospitality Services",
             "description": "Industrial catering clients across Daman union territory - serving leading companies in energy, manufacturing, consumer goods, and electrical sectors.",
             "url": "https://laxmihospitalityservices.in/clients/daman",
             "about": { "@id": "https://laxmihospitalityservices.in/#organization" },
             "publisher": { "@id": "https://laxmihospitalityservices.in/#organization" },
             "isPartOf": {
               "@type": "WebPage",
               "@id": "https://laxmihospitalityservices.in/clients",
               "name": "Our Clients Across India"
             },
             "mainEntity": {
               "@type": "ItemList",
               "name": "Daman Industrial Clients",
               "description": "Leading industrial companies served by Laxmi Hospitality Services across Daman",
               "numberOfItems": 7,
               "itemListElement": [
                 {
                   "@type": "Organization",
                   "name": "Suzlon Energy Ltd.",
                   "description": "Renewable energy leader across multiple Daman locations serving 2,200+ people combined",
                   "address": {
                     "@type": "PostalAddress",
                     "addressLocality": "Beslor",
                     "addressRegion": "Daman and Diu",
                     "addressCountry": "IN"
                   }
                 },
                 {
                   "@type": "Organization",
                   "name": "Zobele India Pvt. Ltd.",
                   "description": "Consumer goods manufacturer in Panchal serving 800+ people",
                   "address": {
                     "@type": "PostalAddress",
                     "addressLocality": "Panchal",
                     "addressRegion": "Daman and Diu",
                     "addressCountry": "IN"
                   }
                 },
                 {
                   "@type": "Organization",
                   "name": "Polycab Wires Pvt. Ltd.",
                   "description": "Electrical manufacturing leader in Daman serving 500+ people",
                   "address": {
                     "@type": "PostalAddress",
                     "addressLocality": "Daman",
                     "addressRegion": "Daman and Diu",
                     "addressCountry": "IN"
                   }
                 },
                 {
                   "@type": "Organization",
                   "name": "Nelson Global India Ltd.",
                   "description": "Manufacturing excellence in Dalwada serving 450+ people",
                   "address": {
                     "@type": "PostalAddress",
                     "addressLocality": "Dalwada",
                     "addressRegion": "Daman and Diu",
                     "addressCountry": "IN"
                   }
                 }
               ]
             },
             "sameAs": [
               "https://www.linkedin.com/company/laxmi-hospitality-services/",
               "https://www.instagram.com/laxmi_hospitality_services/"
             ],
             "inLanguage": "en-IN"
           }
         `}
       </script>
     
       {/* -------- DAMAN STATE BREADCRUMB -------- */}
       <script type="application/ld+json">
         {`
           {
             "@context": "https://schema.org",
             "@type": "BreadcrumbList",
             "@id": "https://laxmihospitalityservices.in/#breadcrumb",
             "name": "Website Breadcrumb",
             "itemListElement": [
               { "@type": "ListItem", "position": 1, "name": "Home",    "item": "https://laxmihospitalityservices.in/" },
               { "@type": "ListItem", "position": 2, "name": "Clients", "item": "https://laxmihospitalityservices.in/clients" },
               { "@type": "ListItem", "position": 3, "name": "Daman",   "item": "https://laxmihospitalityservices.in/clients/daman" }
             ]
           }
         `}
       </script>
     </Helmet>
     
    <div className="dcp-page-container">
      {/* Hero Section */}
      <section className="dcp-hero-section">
        <img
          src="/images/Daman.png" // Reusing the same placeholder image
          alt="Daman Industry Landscape"
          className="dcp-hero-image"
          onError={(e) => { e.target.onerror = null; e.target.src = "https://placehold.co/1920x600/f0f0f0/333333?text=Daman+Hero"; }}
        />
        <div className="dcp-hero-overlay">
          <h2 ref={dcpHeroTitleRef} className="dcp-hero-title">Our Clients in Daman</h2>
          <p ref={dcpHeroBreadcrumbRef} className="dcp-breadcrumb">
            <a href="/" className="dcp-breadcrumb-link">Home</a> /
            <a href="/clients" className="dcp-breadcrumb-link"> Clients</a> / Daman
          </p>
        </div>
      </section>

      {/* Introductory Sentence for Stats Section */}
      <section className="dcp-intro-stats-section">
        <div className="dcp-container">
          <p className="dcp-intro-stats-text">
            In the vibrant union territory of Daman, we proudly partner with a diverse range of industries, contributing to the growth and operational excellence of businesses serving thousands of individuals.
          </p>
        </div>
      </section>

      {/* Stats Section */}
      <section className="dcp-stats-section">
        <div className="dcp-container">
          <div className="dcp-stats-grid">
            <div className="dcp-stat-card">
              <div className="dcp-stat-number">{counters.clients}</div>
              <div className="dcp-stat-label">Clients</div>
            </div>
            <div className="dcp-stat-card">
              <div className="dcp-stat-number">{counters.peopleServed.toLocaleString()}</div>
              <div className="dcp-stat-label">People Serving</div>
            </div>
            <div className="dcp-stat-card">
              <div className="dcp-stat-number">{counters.locations}</div>
              <div className="dcp-stat-label">Locations</div>
            </div>
          </div>
        </div>
      </section>

      {/* Clients Section */}
      <section className="dcp-clients-section">
        <div className="dcp-container">
          <h2 className="dcp-clients-section-title">Our Valued Clients</h2>
          <p className="dcp-clients-section-intro">
            Explore our partnerships with leading companies in Daman, driving innovation and success across various sectors.
          </p>
          <div className="dcp-clients-grid">
            {damanClients
               .slice()
               .sort((a, b) => b.persons - a.persons)
               .map((client, index) => (
              <div
                key={index}
                className={`dcp-client-card ${index < visibleCards ? 'dcp-visible' : ''}`}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                {/* Client Logo at the Top - Prominent */}
                <div className="dcp-card-logo-wrapper">
                  <img
                    src={client.logo}
                    alt={`${client.name} Logo`}
                    className="dcp-card-logo-image"
                    onError={(e) => { e.target.onerror = null; e.target.src = `https://placehold.co/150x100/e0e0e0/333333?text=${client.name.split(' ')[0]}`; }}
                  />
                </div>

                {/* Client Info below logo */}
                <div className="dcp-client-info-v2">
                  <h3 className="dcp-client-name-v2">{client.name}</h3>
                  <div className="dcp-location-v2">
                    <FaMapMarkerAlt className="dcp-icon-v2" />
                    {client.location}
                  </div>
                  <div className="dcp-people-served">
                    <FaUsers className="dcp-icon-v2" />
                    {client.persons.toLocaleString()} People Serving
                  </div>
                  <div className="dcp-type-tag">
                    Type: {client.industry}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      <ClientBranches/>
      <CompanyProfile1/>
    </div>
    </>
  );
};

export default DamanClientsPage;
